import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Plus, PawPrint, Pencil, Trash2 } from "lucide-react";
import { toast } from "sonner";

const EMPTY = { name: "", species: "Dog", breed: "", age: "", weight: "", gender: "", photo: "", vet_name: "", vet_contact: "", medical_notes: "", notes: "" };

export default function Pets() {
  const [pets, setPets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);

  const load = () => { setLoading(true); api.get("/pets").then((r) => { setPets(r.data); setLoading(false); }); };
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm(EMPTY); setEditing(null); setOpen(true); };
  const openEdit = (p) => { setForm({ ...EMPTY, ...p }); setEditing(p.pet_id); setOpen(true); };

  const submit = async (e) => {
    e.preventDefault();
    try {
      if (editing) await api.patch(`/pets/${editing}`, form);
      else await api.post("/pets", form);
      toast.success(editing ? "Pet updated" : "Pet added");
      setOpen(false);
      load();
    } catch { toast.error("Save failed"); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this pet profile?")) return;
    await api.delete(`/pets/${id}`);
    toast.success("Pet removed");
    load();
  };

  return (
    <div data-testid="pets-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <div className="eyebrow mb-2">Pet Management</div>
          <h1 className="font-display text-3xl font-semibold text-slate-900 tracking-tight">Your pets.</h1>
        </div>
        <Button data-testid="add-pet-btn" onClick={openNew} className="h-11 px-5 bg-blue-600 hover:bg-blue-700 text-white rounded-md">
          <Plus className="w-4 h-4 mr-1.5" strokeWidth={1.75}/> Add pet
        </Button>
      </div>

      {loading ? (
        <div className="text-slate-400 font-mono text-xs uppercase tracking-widest">Loading pets…</div>
      ) : pets.length === 0 ? (
        <div className="border border-dashed border-slate-200 rounded-md p-12 text-center">
          <PawPrint className="w-8 h-8 text-slate-300 mx-auto mb-3" strokeWidth={1.5}/>
          <div className="text-sm text-slate-600">No pets added yet. Start with your first pet profile.</div>
        </div>
      ) : (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          {pets.map((p) => (
            <div key={p.pet_id} data-testid={`pet-${p.pet_id}`} className="border border-slate-200 rounded-md p-5 bg-white card-clinical">
              <div className="flex items-start justify-between">
                <div className="flex items-center gap-3 min-w-0">
                  {p.photo ? (
                    <img src={p.photo} alt="" className="w-12 h-12 rounded-md object-cover"/>
                  ) : (
                    <div className="w-12 h-12 rounded-md bg-blue-50 border border-blue-200 flex items-center justify-center">
                      <PawPrint className="w-5 h-5 text-blue-700" strokeWidth={1.5}/>
                    </div>
                  )}
                  <div className="min-w-0">
                    <div className="font-display text-lg font-semibold text-slate-900 truncate">{p.name}</div>
                    <div className="text-xs text-slate-500">{[p.species, p.breed].filter(Boolean).join(" · ") || "—"}</div>
                  </div>
                </div>
                <div className="flex gap-1">
                  <button onClick={() => openEdit(p)} className="p-1.5 rounded-md hover:bg-slate-100" data-testid={`edit-pet-${p.pet_id}`}><Pencil className="w-3.5 h-3.5 text-slate-500" strokeWidth={1.75}/></button>
                  <button onClick={() => del(p.pet_id)} className="p-1.5 rounded-md hover:bg-red-50" data-testid={`del-pet-${p.pet_id}`}><Trash2 className="w-3.5 h-3.5 text-red-500" strokeWidth={1.75}/></button>
                </div>
              </div>
              <div className="mt-4 grid grid-cols-2 gap-3 text-xs">
                {p.age && <div><span className="text-slate-400">Age</span><div className="text-slate-900">{p.age}</div></div>}
                {p.weight && <div><span className="text-slate-400">Weight</span><div className="text-slate-900">{p.weight}</div></div>}
                {p.gender && <div><span className="text-slate-400">Gender</span><div className="text-slate-900">{p.gender}</div></div>}
                {p.vet_name && <div><span className="text-slate-400">Vet</span><div className="text-slate-900 truncate">{p.vet_name}</div></div>}
              </div>
              {p.medical_notes && (
                <div className="mt-3 pt-3 border-t border-slate-100 text-xs text-slate-600 line-clamp-3">{p.medical_notes}</div>
              )}
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl rounded-md">
          <DialogHeader>
            <DialogTitle className="font-display text-2xl">{editing ? "Edit pet" : "Add pet"}</DialogTitle>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-3">
            <div className="grid grid-cols-2 gap-3">
              <Input data-testid="pet-name" required placeholder="Name" value={form.name} onChange={(e) => setForm({...form, name: e.target.value})}/>
              <Input placeholder="Species (Dog, Cat…)" value={form.species} onChange={(e) => setForm({...form, species: e.target.value})}/>
              <Input placeholder="Breed" value={form.breed} onChange={(e) => setForm({...form, breed: e.target.value})}/>
              <Input placeholder="Age (e.g. 4 years)" value={form.age} onChange={(e) => setForm({...form, age: e.target.value})}/>
              <Input placeholder="Weight (e.g. 22 kg)" value={form.weight} onChange={(e) => setForm({...form, weight: e.target.value})}/>
              <Input placeholder="Gender" value={form.gender} onChange={(e) => setForm({...form, gender: e.target.value})}/>
              <Input placeholder="Photo URL (optional)" value={form.photo} onChange={(e) => setForm({...form, photo: e.target.value})} className="col-span-2"/>
              <Input placeholder="Veterinarian name" value={form.vet_name} onChange={(e) => setForm({...form, vet_name: e.target.value})}/>
              <Input placeholder="Vet contact / phone / email" value={form.vet_contact} onChange={(e) => setForm({...form, vet_contact: e.target.value})}/>
            </div>
            <Textarea rows={3} placeholder="Medical notes" value={form.medical_notes} onChange={(e) => setForm({...form, medical_notes: e.target.value})}/>
            <Textarea rows={2} placeholder="Notes" value={form.notes} onChange={(e) => setForm({...form, notes: e.target.value})}/>
            <div className="flex gap-2 justify-end pt-2">
              <Button type="button" variant="ghost" onClick={() => setOpen(false)}>Cancel</Button>
              <Button data-testid="pet-save" type="submit" className="bg-blue-600 hover:bg-blue-700 text-white">{editing ? "Save" : "Add"}</Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
